import React, { useEffect, useState } from "react";
import { prettifyStatusLabel } from "./prettifyStatusLabel";
import CompanyOnePager from "./CompanyOnePager";
import { apiFetch, getApiBase, parseJsonOrExplain } from "./apiBase";

// Ingen trigger-snapshot, plattformsregler eller tom bolagsanalys-agent — börjar vid bolagssnapshot.
const IC_FLOW_STEPS = [
  { key: "company_snapshot", title: "Bolag (snapshot vid körning)" },
  { key: "ic_analyst_output", title: "IC-analys" },
  { key: "red_team_output", title: "Red team" },
  { key: "ic_analyst_rebuttal_output", title: "Rebuttal" },
  { key: "ic_agent_output", title: "Slutligt IC-beslut" },
];

const IC_ANALYST_COMPANY_STRIP = new Set([
  "Name",
  "Country",
  "Industry",
  "Employees",
  "Net Sales (M)",
  "EBITDA (M)",
]);

const MAX_JSON_DEPTH = 9;

function humanizeFieldKey(k) {
  if (!k || typeof k !== "string") return "";
  if (k.includes("_")) return prettifyStatusLabel(k);
  return k;
}

const OWNERSHIP_FIELDS = [
  "ownership_status",
  "ownership_verified",
  "recent_transaction_found",
  "ownership_notes",
];

const OWNERSHIP_BOOLEAN_FIELDS = ["ownership_verified", "recent_transaction_found"];

function filterCompanySnapshotForDisplay(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const out = { ...data };
  delete out.batch_no;
  // Lyft upp ägarfälten från verify-svaret så de visas, innan resten av råsvaret tas bort.
  const verified = out.verified_raw_json;
  if (verified && typeof verified === "object" && !Array.isArray(verified)) {
    OWNERSHIP_FIELDS.forEach((key) => {
      if (out[key] === undefined && verified[key] !== undefined) {
        out[key] = verified[key];
      }
    });
  }
  OWNERSHIP_BOOLEAN_FIELDS.forEach((key) => {
    if (typeof out[key] === "boolean") {
      out[key] = out[key] ? "Ja" : "Nej";
    }
  });
  delete out.verified_raw_json;
  OWNERSHIP_FIELDS.forEach((key) => {
    const val = out[key];
    if (val === undefined || val === null || String(val).trim() === "") {
      delete out[key];
    }
  });
  if (out.website !== undefined && String(out.website).trim() === "") {
    delete out.website;
  }
  if (out.url !== undefined && String(out.url).trim() === "") {
    delete out.url;
  }
  if (out.why_it_may_not_fit !== undefined && String(out.why_it_may_not_fit).trim() === "") {
    delete out.why_it_may_not_fit;
  }
  return out;
}

function filterIcAnalystForDisplay(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const out = { ...data };
  if (out.Company != null) {
    if (typeof out.Company === "string") {
      delete out.Company;
    } else if (typeof out.Company === "object" && !Array.isArray(out.Company)) {
      const rest = { ...out.Company };
      IC_ANALYST_COMPANY_STRIP.forEach((key) => {
        delete rest[key];
      });
      if (Object.keys(rest).length === 0) {
        delete out.Company;
      } else {
        out.Company = rest;
      }
    }
  }
  return out;
}

function flowStepData(icFlowDetail, stepKey) {
  const raw = icFlowDetail[stepKey];
  if (stepKey === "company_snapshot") return filterCompanySnapshotForDisplay(raw);
  if (stepKey === "ic_analyst_output") return filterIcAnalystForDisplay(raw);
  return raw;
}

function isEmptyFlowStep(data) {
  if (data == null) return true;
  if (typeof data === "string" && !String(data).trim()) return true;
  if (typeof data === "object" && !Array.isArray(data) && Object.keys(data).length === 0) {
    return true;
  }
  return false;
}

function JsonKeyValue({ data, depth }) {
  if (data === null || data === undefined) return null;
  if (depth > MAX_JSON_DEPTH) {
    return (
      <span style={{ color: "#9a9288", fontSize: "13px", fontFamily: 'Arial, Helvetica, sans-serif' }}>
        …
      </span>
    );
  }

  if (typeof data === "object" && data._parse_error) {
    return (
      <pre
        style={{
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontSize: "12px",
          lineHeight: 1.5,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          color: "#333",
        }}
      >
        {String(data._raw || "")}
      </pre>
    );
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    return (
      <ul
        style={{
          margin: "6px 0 0 0",
          paddingLeft: "18px",
          fontFamily: 'Arial, Helvetica, sans-serif',
          lineHeight: 1.55,
        }}
      >
        {data.map((item, i) => (
          <li key={i} style={{ marginBottom: "6px" }}>
            <JsonKeyValue data={item} depth={depth + 1} />
          </li>
        ))}
      </ul>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (entries.length === 0) return null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {entries.map(([k, v]) => (
          <div key={k}>
            <div
              style={{
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "#5f5a52",
                marginBottom: "4px",
                fontFamily: 'Arial, Helvetica, sans-serif',
              }}
            >
              {humanizeFieldKey(k)}
            </div>
            <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: 1.55 }}>
              <JsonKeyValue data={v} depth={depth + 1} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const str = String(data);
  if (/^[a-zA-Z0-9_]+$/.test(str) && str.includes("_")) {
    return <span>{prettifyStatusLabel(str)}</span>;
  }
  return <span>{str}</span>;
}

function FlowJsonBlock({ title, data }) {
  const empty = isEmptyFlowStep(data);
  return (
    <div
      style={{
        marginBottom: "18px",
        paddingBottom: "18px",
        borderBottom: "1px solid rgba(17,17,17,0.08)",
      }}
    >
      <div
        style={{
          fontSize: "12px",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: "#5f5a52",
          marginBottom: "10px",
          fontFamily: 'Arial, Helvetica, sans-serif',
        }}
      >
        {title}
      </div>
      {empty ? (
        <div
          style={{
            fontSize: "13px",
            color: "#9a9288",
            fontFamily: 'Arial, Helvetica, sans-serif',
          }}
        >
          Ingen data för detta steg.
        </div>
      ) : (
        <JsonKeyValue data={data} depth={0} />
      )}
    </div>
  );
}

function panelStyle(fullPageOnePager) {
  if (fullPageOnePager) {
    return {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: "100%",
      maxWidth: "100%",
      height: "100%",
      background: "#fbfaf7",
      overflowY: "auto",
      padding: "24px 32px 56px",
      zIndex: 1000,
      boxSizing: "border-box",
      pointerEvents: "auto",
    };
  }
  return {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: "auto",
    width: "min(560px, 100vw)",
    maxWidth: "100%",
    height: "100%",
    background: "#fbfaf7",
    borderLeft: "1px solid rgba(17,17,17,0.10)",
    overflowY: "auto",
    padding: "24px",
    zIndex: 1000,
    boxShadow: "-10px 0 30px rgba(0,0,0,0.08)",
    pointerEvents: "auto",
  };
}

function cardStyle() {
  return {
    background: "#fff",
    border: "1px solid rgba(17,17,17,0.10)",
    borderRadius: "18px",
    padding: "16px",
    marginBottom: "14px",
  };
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function formatSek(value) {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString("sv-SE") + " SEK";
}

function normalizeUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return "https://" + url;
}

function CompanyProfile({ companyId, onClose }) {
  const [company, setCompany] = useState(null);
  const [history, setHistory] = useState([]);
  const [manual, setManual] = useState([]);

  const [decision, setDecision] = useState("");
  const [reasonCategory, setReasonCategory] = useState("");
  const [comment, setComment] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [selectedIcCaseId, setSelectedIcCaseId] = useState(null);
  const [icFlowDetail, setIcFlowDetail] = useState(null);
  const [icFlowLoading, setIcFlowLoading] = useState(false);
  const [icFlowError, setIcFlowError] = useState(null);
  const [profileTab, setProfileTab] = useState("profile");

  const loadData = () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    const api = getApiBase();
    Promise.all([
      apiFetch(`${api}/companies/${encodeURIComponent(companyId)}`).then((res) =>
        parseJsonOrExplain(res)
      ),
      apiFetch(`${api}/companies/${encodeURIComponent(companyId)}/process_history`).then((res) =>
        parseJsonOrExplain(res)
      ),
      apiFetch(`${api}/companies/${encodeURIComponent(companyId)}/manual_analysis`).then((res) =>
        parseJsonOrExplain(res)
      ),
    ])
      .then(([c, h, m]) => {
        setCompany(c);
        setHistory(h);
        setManual(m);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Fel vid hämtning");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, [companyId]);

  useEffect(() => {
    setSelectedIcCaseId(null);
    setProfileTab("profile");
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !selectedIcCaseId) {
      setIcFlowDetail(null);
      setIcFlowError(null);
      setIcFlowLoading(false);
      return;
    }

    setIcFlowLoading(true);
    setIcFlowError(null);
    setIcFlowDetail(null);

    apiFetch(`${getApiBase()}/companies/${encodeURIComponent(companyId)}/ic_cases/${selectedIcCaseId}`)
      .then((res) => parseJsonOrExplain(res))
      .then((data) => {
        setIcFlowDetail(data);
        setIcFlowLoading(false);
      })
      .catch((err) => {
        setIcFlowError(err.message || "Fel vid hämtning");
        setIcFlowLoading(false);
      });
  }, [companyId, selectedIcCaseId]);

  const submitManual = () => {
    if (!decision) {
      setError("Välj beslut");
      return;
    }

    setSaving(true);
    setError(null);

    apiFetch(`${getApiBase()}/companies/${encodeURIComponent(companyId)}/manual_analysis`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        decision,
        reason_category: reasonCategory,
        comment,
        user: "erik",
      }),
    })
      .then((res) => parseJsonOrExplain(res))
      .then(() => {
        setDecision("");
        setReasonCategory("");
        setComment("");
        return apiFetch(`${getApiBase()}/companies/${encodeURIComponent(companyId)}/manual_analysis`);
      })
      .then((res) => parseJsonOrExplain(res))
      .then((data) => {
        setManual(data);
        setSaving(false);
      })
      .catch((err) => {
        setError(err.message || "Fel vid sparande");
        setSaving(false);
      });
  };

  if (!companyId) return null;

  const onePagerActive = profileTab === "onepager";

  return (
    <div style={panelStyle(onePagerActive)}>
      <div
        style={{
          maxWidth: onePagerActive ? "1280px" : undefined,
          margin: onePagerActive ? "0 auto" : undefined,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            gap: "12px",
            marginBottom: onePagerActive ? "28px" : "18px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1 1 240px", minWidth: 0 }}>
            <div
              style={{
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: "#5f5a52",
                fontFamily: 'Arial, Helvetica, sans-serif',
                marginBottom: "8px",
              }}
            >
              {onePagerActive ? "Investment one-pager" : "Företagsprofil"}
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: onePagerActive ? "28px" : "34px",
                lineHeight: 1.05,
                fontWeight: 500,
              }}
            >
              {company?.company_name || companyId}
            </h2>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {onePagerActive && (
              <span
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.15em",
                  color: "#8a8278",
                  border: "1px solid rgba(17,17,17,0.2)",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  fontFamily: 'Arial, Helvetica, sans-serif',
                }}
              >
                CONFIDENTIAL
              </span>
            )}
            <div style={{ display: "flex", gap: "6px", fontFamily: 'Arial, Helvetica, sans-serif' }}>
              <button
                type="button"
                onClick={() => setProfileTab("profile")}
                style={{
                  padding: "8px 14px",
                  borderRadius: "999px",
                  border: "1px solid rgba(17,17,17,0.18)",
                  background: profileTab === "profile" ? "#111" : "#fff",
                  color: profileTab === "profile" ? "#fff" : "#111",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                Profil
              </button>
              <button
                type="button"
                onClick={() => setProfileTab("onepager")}
                style={{
                  padding: "8px 14px",
                  borderRadius: "999px",
                  border: "1px solid rgba(17,17,17,0.18)",
                  background: profileTab === "onepager" ? "#111" : "#fff",
                  color: profileTab === "onepager" ? "#fff" : "#111",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                One-pager
              </button>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: "10px 14px",
                borderRadius: "999px",
                border: "1px solid rgba(17,17,17,0.18)",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Stäng
            </button>
          </div>
        </div>

        {profileTab === "onepager" && (
          <CompanyOnePager
            companyId={companyId}
            companyName={company?.company_name}
            omitDocumentHeader
          />
        )}

      {profileTab === "profile" && loading && <p>Laddar...</p>}
      {profileTab === "profile" && error && <p>{error}</p>}

      {profileTab === "profile" && !loading && company && (
        <>
          <div style={cardStyle()}>
            <div
              style={{
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: "#5f5a52",
                fontFamily: 'Arial, Helvetica, sans-serif',
                marginBottom: "10px",
              }}
            >
              Grundinfo
            </div>

            <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: 1.8 }}>
              <div><strong>ID:</strong> {company.company_id || "-"}</div>
              <div><strong>Namn:</strong> {company.company_name || "-"}</div>
              <div><strong>Omsättning:</strong> {formatSek(company.net_sales)}</div>
              <div><strong>EBITDA:</strong> {formatSek(company.ebitda)}</div>
              {company.website && (
                <div>
                  <strong>Webb:</strong>{" "}
                  <a
                    href={normalizeUrl(company.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {company.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          <div style={cardStyle()}>
            <div
              style={{
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: "#5f5a52",
                fontFamily: 'Arial, Helvetica, sans-serif',
                marginBottom: "10px",
              }}
            >
              IC-historik
            </div>

            {selectedIcCaseId ? (
              <>
                <button
                  type="button"
                  onClick={() => setSelectedIcCaseId(null)}
                  style={{
                    marginBottom: "14px",
                    padding: "8px 14px",
                    borderRadius: "999px",
                    border: "1px solid rgba(17,17,17,0.2)",
                    background: "#fff",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontFamily: 'Arial, Helvetica, sans-serif',
                  }}
                >
                  Tillbaka till listan
                </button>

                {icFlowLoading && (
                  <p style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>Laddar flöde…</p>
                )}
                {icFlowError && (
                  <p style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>{icFlowError}</p>
                )}

                {icFlowDetail && !icFlowLoading && (
                  <>
                    <div
                      style={{
                        marginBottom: "18px",
                        paddingBottom: "16px",
                        borderBottom: "1px solid rgba(17,17,17,0.10)",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      <div style={{ fontSize: "18px", fontWeight: 500, marginBottom: "8px" }}>
                        {icFlowDetail.trigger_title?.trim() || "IC-flöde"}
                      </div>
                      <div style={{ fontSize: "14px", color: "#333", marginBottom: "4px" }}>
                        Utfall:{" "}
                        <strong>
                          {prettifyStatusLabel(icFlowDetail.ic_decision || icFlowDetail.status || "–")}
                        </strong>
                        {icFlowDetail.ic_confidence != null && icFlowDetail.ic_confidence !== "" && (
                          <span style={{ color: "#5f5a52" }}>
                            {" "}
                            · Konfidens: {String(icFlowDetail.ic_confidence)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "13px", color: "#5f5a52" }}>
                        {formatDate(icFlowDetail.created_at)}
                        {icFlowDetail.status && icFlowDetail.status !== icFlowDetail.ic_decision && (
                          <span> · Status: {prettifyStatusLabel(icFlowDetail.status)}</span>
                        )}
                      </div>
                      {icFlowDetail.retry_count > 0 && (
                        <div style={{ fontSize: "12px", color: "#b54708", marginTop: "6px" }}>
                          Omkörningar: {icFlowDetail.retry_count}
                        </div>
                      )}
                      {icFlowDetail.last_error_text?.trim() && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#b42318",
                            marginTop: "8px",
                            lineHeight: 1.45,
                          }}
                        >
                          Fel: {icFlowDetail.last_error_text}
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: "#5f5a52",
                        marginBottom: "12px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      Flöde till beslut
                    </div>

                    {IC_FLOW_STEPS.map((step) => (
                      <FlowJsonBlock
                        key={step.key}
                        title={step.title}
                        data={flowStepData(icFlowDetail, step.key)}
                      />
                    ))}
                  </>
                )}
              </>
            ) : history.length === 0 ? (
              <div style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                Ingen historik hittades.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#7a746c",
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    marginBottom: "4px",
                  }}
                >
                  Klicka på en rad för att se hela flödet och resonemang per steg.
                </div>
                {history.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => setSelectedIcCaseId(h.id)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      background: "#faf9f6",
                      border: "1px solid rgba(17,17,17,0.08)",
                      borderRadius: "12px",
                      padding: "12px 12px 12px 14px",
                      cursor: "pointer",
                      font: "inherit",
                      color: "inherit",
                    }}
                  >
                    <div
                      style={{
                        borderLeft: "3px solid #111",
                        paddingLeft: "12px",
                      }}
                    >
                      <div style={{ fontSize: "17px", marginBottom: "4px" }}>
                        {prettifyStatusLabel(h.ic_decision || h.status || "-")}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#5f5a52",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          marginBottom: "4px",
                        }}
                      >
                        {formatDate(h.created_at)}
                      </div>
                      {h.status && (
                        <div
                          style={{
                            fontFamily: 'Arial, Helvetica, sans-serif',
                            fontSize: "14px",
                            marginBottom: "2px",
                          }}
                        >
                          Status: {prettifyStatusLabel(h.status)}
                        </div>
                      )}
                      {h.fingerprint && (
                        <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: "14px" }}>
                          Trigger:{" "}
                          {typeof h.trigger_title === "string" && h.trigger_title.trim()
                            ? h.trigger_title.trim()
                            : h.fingerprint}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={cardStyle()}>
            <div
              style={{
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: "#5f5a52",
                fontFamily: 'Arial, Helvetica, sans-serif',
                marginBottom: "10px",
              }}
            >
              Ny manuell analys
            </div>

            <div style={{ marginBottom: "10px" }}>
              <select
                value={decision}
                onChange={(e) => setDecision(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: "12px",
                  border: "1px solid rgba(17,17,17,0.14)",
                  background: "#fff",
                  fontSize: "14px",
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  boxSizing: "border-box",
                }}
              >
                <option value="">Välj beslut</option>
                <option value="interesting">Intressant</option>
                <option value="not_interesting">Ej intressant</option>
                <option value="uncertain">Osäker</option>
              </select>
            </div>

            <div style={{ marginBottom: "10px" }}>
              <select
                value={reasonCategory}
                onChange={(e) => setReasonCategory(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: "12px",
                  border: "1px solid rgba(17,17,17,0.14)",
                  background: "#fff",
                  fontSize: "14px",
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  boxSizing: "border-box",
                }}
              >
                <option value="">Välj kategori</option>
                <option value="market">Marknad</option>
                <option value="size">Storlek</option>
                <option value="sector_fit">Sektorfitt</option>
                <option value="business_quality">Kvalitet</option>
                <option value="ownership">Ägarsituation</option>
                <option value="too_broad">För bred</option>
                <option value="too_early">För tidig</option>
                <option value="other">Övrigt</option>
              </select>
            </div>

            <div style={{ marginBottom: "10px" }}>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Kommentar..."
                style={{
                  width: "100%",
                  height: "120px",
                  padding: "12px 14px",
                  borderRadius: "12px",
                  border: "1px solid rgba(17,17,17,0.14)",
                  background: "#fff",
                  fontSize: "14px",
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  boxSizing: "border-box",
                  resize: "vertical",
                }}
              />
            </div>

            <button
              onClick={submitManual}
              disabled={saving}
              style={{
                padding: "12px 18px",
                borderRadius: "999px",
                border: "1px solid #111",
                background: "#111",
                color: "#fff",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              {saving ? "Sparar..." : "Spara analys"}
            </button>
          </div>

          <div style={cardStyle()}>
            <div
              style={{
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: "#5f5a52",
                fontFamily: 'Arial, Helvetica, sans-serif',
                marginBottom: "10px",
              }}
            >
              Tidigare manuell analys
            </div>

            {manual.length === 0 ? (
              <div style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                Ingen manuell analys sparad ännu.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {manual.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "12px",
                      borderRadius: "12px",
                      background: "#f8f6f2",
                      border: "1px solid rgba(17,17,17,0.08)",
                    }}
                  >
                    <div style={{ fontSize: "16px", marginBottom: "4px" }}>
                      {prettifyStatusLabel(m.decision || "-")}
                    </div>
                    {m.reason_category && (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#5f5a52",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          marginBottom: "4px",
                        }}
                      >
                        Kategori: {prettifyStatusLabel(m.reason_category)}
                      </div>
                    )}
                    {m.comment && (
                      <div
                        style={{
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          lineHeight: 1.6,
                          marginBottom: "4px",
                        }}
                      >
                        {m.comment}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#5f5a52",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      {formatDate(m.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      </div>
    </div>
  );
}

export default CompanyProfile;
